/**
 * docViewers — dispatch table for the inline document preview.
 *
 * Each viewer is loaded lazily so the heavyweight deps (pdfjs-dist,
 * mammoth, xlsx) only land in the bundle the first time a user
 * actually opens a doc of that type. Vite splits each `import()`
 * boundary into its own chunk automatically.
 *
 * resolveViewer() is a pure function: takes a SiteDocument, returns
 * the React component that knows how to render it. Callers wrap the
 * returned component in <Suspense>.
 */
import React, { lazy } from 'react';
import type { SiteDocument } from '../../../types/siteRag';
import { retryImport } from './retryImport';

/**
 * REF-LINEAGE REF-3 — every viewer signature accepts `initialPage?:
 * number` so the DocViewerDialog can thread the prop through without
 * branching on viewer kind. Only `PdfViewer` consumes the value; the
 * other viewers ignore it (page-jump is PDF-only — DOCX / spreadsheet
 * / text page numbers are ambiguous, and image / video have no
 * pagination concept). Each non-PDF viewer adds a one-line
 * `// REF-3 — page-jump is PDF-only; ignored.` comment on its
 * signature to keep the contract obvious.
 */
export type ViewerComponent = React.LazyExoticComponent<React.ComponentType<{ doc: SiteDocument; initialPage?: number }>>;

export const VIEWER_KIND = {
  pdf: 'PdfViewer',
  docx: 'DocxViewer',
  sheet: 'SheetViewer',
  image: 'ImageViewer',
  video: 'VideoViewer',
  text: 'TextViewer',
  officePreview: 'OfficePreviewViewer',
  fallback: 'FallbackViewer',
} as const;
export type ViewerKind = typeof VIEWER_KIND[keyof typeof VIEWER_KIND];

// retryImport() wraps each dynamic import with a one-shot retry — a
// transient chunk-fetch blip recovers silently; a genuinely-missing
// chunk (stale deploy) rethrows so the global vite:preloadError
// handler (App.tsx) and the DocViewerDialog ErrorBoundary take over.
const VIEWERS: Record<ViewerKind, ViewerComponent> = {
  [VIEWER_KIND.pdf]:      lazy(retryImport(() => import('./PdfViewer'))),
  [VIEWER_KIND.docx]:     lazy(retryImport(() => import('./DocxViewer'))),
  [VIEWER_KIND.sheet]:    lazy(retryImport(() => import('./SheetViewer'))),
  [VIEWER_KIND.image]:    lazy(retryImport(() => import('./ImageViewer'))),
  [VIEWER_KIND.video]:    lazy(retryImport(() => import('./VideoViewer'))),
  [VIEWER_KIND.text]:     lazy(retryImport(() => import('./TextViewer'))),
  [VIEWER_KIND.officePreview]: lazy(retryImport(() => import('./OfficePreviewViewer'))),
  [VIEWER_KIND.fallback]: lazy(retryImport(() => import('./FallbackViewer'))),
};

/**
 * Pure dispatch — same in / same out, side-effect free. Tested
 * directly via classifyViewer (no need to mount React) so we can
 * pin the format → renderer mapping at unit-test time without
 * dragging in lazy() fallthrough behaviour.
 */
export function classifyViewer(doc: Pick<SiteDocument, 'fileName' | 'mimeType'>): ViewerKind {
  const ext = doc.fileName.split('.').pop()?.toLowerCase() ?? '';
  const mime = (doc.mimeType ?? '').toLowerCase();
  if (mime === 'application/pdf' || ext === 'pdf') return VIEWER_KIND.pdf;
  // mammoth parses OOXML .docx only. Legacy binary .doc (OLE) and .rtf
  // must NOT reach DocxViewer (it would throw) — route them to the
  // honest Fallback "legacy format — download / re-save as .docx" panel.
  // Order matters: catch the legacy formats BEFORE the .docx branch.
  if (ext === 'doc' || ext === 'rtf' || mime === 'application/msword' || mime === 'application/rtf' || mime === 'text/rtf') {
    return VIEWER_KIND.fallback;
  }
  if (ext === 'docx' || mime.includes('wordprocessingml')) return VIEWER_KIND.docx;
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv' || mime.includes('spreadsheet') || mime.includes('excel')) {
    return VIEWER_KIND.sheet;
  }
  // PowerPoint — Office_Preview. No client-side inline renderer, so route
  // to OfficePreviewViewer which surfaces a "Generate preview" affordance
  // (server-side LibreOffice → PDF) and, once ready, reuses PdfViewer to
  // render the derived PDF. OfficePreviewViewer handles every preview
  // state (none/pending/ready/failed) internally — including the
  // download-only fallback when the doc can't be converted — so we route
  // here unconditionally for ppt/pptx. (Caught before image/video/text so
  // the presentationml mime never falls through.)
  if (ext === 'pptx' || ext === 'ppt' || mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint') {
    return VIEWER_KIND.officePreview;
  }
  if (mime.startsWith('image/') || /^(png|jpe?g|gif|svg|bmp|webp)$/.test(ext)) return VIEWER_KIND.image;
  if (mime.startsWith('video/') || /^(mp4|webm|mov|avi)$/.test(ext)) return VIEWER_KIND.video;
  if (/^(txt|md|json|log|html|xml)$/.test(ext) || mime.startsWith('text/')) return VIEWER_KIND.text;
  return VIEWER_KIND.fallback;
}

export function resolveViewer(doc: SiteDocument): ViewerComponent {
  return VIEWERS[classifyViewer(doc)];
}
